// Downscales a photo File client-side before it's ever held as a full-resolution base64 string in
// React state (or uploaded). This matters specifically for Android: a modern phone camera photo
// is commonly 4000x3000px / several MB, and forcing the direct-camera-capture intent via
// <input capture> on a low-RAM Android device is a well-known trigger for Chrome's "Unable to
// load. There's not enough available memory" failure — the OS can't hand the full-res photo back
// to the tab. Removing `capture` (so Android shows its normal Camera/Files/Gallery picker instead
// of the heavyweight forced-capture intent) is the other half of this fix, done at the call sites;
// this halves memory pressure for whatever does come through, and keeps upload/AI-estimate
// payloads small.
const MAX_DIMENSION = 1600;
const JPEG_QUALITY = 0.75;

export async function downscaleImage(
    file: File,
    maxDimension = MAX_DIMENSION,
    quality = JPEG_QUALITY
): Promise<{ dataUrl: string; base64: string }> {
    const bitmap = await loadBitmap(file);
    const { width, height } = bitmap;
    const scale = Math.min(1, maxDimension / Math.max(width, height));
    const targetW = Math.max(1, Math.round(width * scale));
    const targetH = Math.max(1, Math.round(height * scale));

    const canvas = document.createElement('canvas');
    canvas.width = targetW;
    canvas.height = targetH;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Could not process that image.');
    ctx.drawImage(bitmap, 0, 0, targetW, targetH);
    if ('close' in bitmap) bitmap.close();

    const dataUrl = canvas.toDataURL('image/jpeg', quality);
    return { dataUrl, base64: dataUrl.split(',')[1] };
}

async function loadBitmap(file: File): Promise<ImageBitmap | HTMLImageElement> {
    if ('createImageBitmap' in window) {
        try { return await createImageBitmap(file); } catch { /* fall through to <img> */ }
    }
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => { URL.revokeObjectURL(img.src); resolve(img); };
        img.onerror = () => { URL.revokeObjectURL(img.src); reject(new Error('Could not read that image.')); };
        img.src = URL.createObjectURL(file);
    });
}
